/**
 * The one place the application talks to the network.
 *
 * Components never call `fetch` themselves: they use the TanStack Query hooks
 * in `queries.ts`, which call through here. f17.3: the session is the API's
 * HttpOnly cookie, which the browser attaches on its own (`same-origin`); this
 * file adds the CSRF header every call carries and sends a dead session back
 * to the sign-in screen. No credential is ever handled in JavaScript.
 */

import { returnToSignIn } from '@/auth/signIn';
import { appConfig } from '@/config';

import type { components, operations } from './generated/schema';

export type HealthResponse = components['schemas']['HealthResponse'];
export type SessionResponse = components['schemas']['SessionResponse'];
export type UserResponse = components['schemas']['UserResponse'];
export type RoleResponse = components['schemas']['RoleResponse'];
export type HealthStatus = components['schemas']['HealthStatus'];

// --- Artifacts (f15.1) -- visibility follows the owning node, server-side.
export type ArtifactListItem = components['schemas']['ArtifactListItem'];
export type ArtifactList = components['schemas']['ArtifactList'];
export type ArtifactDetail = components['schemas']['ArtifactDetailResponse'];
export type ArtifactVersion = components['schemas']['ArtifactVersionResponse'];
/** f15.3: derived from the published pointer, never stored. */
export type VersionPublicationStatus = components['schemas']['VersionPublicationStatus'];
export type ArtifactMetadataUpdate = components['schemas']['ArtifactMetadataUpdate'];
/** f15.2: a node the caller may publish on, for the owner picker. */
export type ArtifactOwnerOption = components['schemas']['OwnerOption'];
export type ArtifactOwnerOptionList = components['schemas']['OwnerOptionList'];
/** f15.4: an access code's label and state -- never the code itself. */
export type AccessPassword = components['schemas']['AccessPassword'];
export type AccessPasswordList = components['schemas']['AccessPasswordList'];
export type IssueAccessPasswordRequest = components['schemas']['IssueAccessPasswordRequest'];
/** The ONLY payload carrying a plaintext code. Keep it in local component state, never in the cache. */
export type IssuedAccessPassword = components['schemas']['IssuedAccessPassword'];
export type AccessPasswordStatusCode = 'ACTIVE' | 'EXPIRED' | 'EXHAUSTED' | 'REVOKED';
/** f15.6: one row of an artifact's access history, projected from the single audit log. */
export type ArtifactAccessEvent = components['schemas']['ArtifactAccessEvent'];
export type ArtifactAccessEventPage = components['schemas']['ArtifactAccessEventPage'];
export type AccessEventKind = components['schemas']['AccessEventKind'];
/** The four levels an artifact can hang off. */
export type ArtifactOwnerType = 'BUSINESS_UNIT_GROUP' | 'BUSINESS_UNIT' | 'CLIENT' | 'ENGAGEMENT';
/** The three nodes that carry an Artifacts tab. */
export type ArtifactListOwnerType = Exclude<ArtifactOwnerType, 'ENGAGEMENT'>;
export interface NewArtifact {
  ownerType: ArtifactOwnerType;
  ownerId: string;
  title: string;
  description?: string;
  releaseNote?: string;
  file: File;
}

// --- The landing dashboard (f16.1) -- scoped server-side; NO_ACCESS carries no number at all.
export type Dashboard = components['schemas']['DashboardNoAccessResponse'] | components['schemas']['DashboardScopedResponse'];
export type DashboardScoped = components['schemas']['DashboardScopedResponse'];
export type DashboardCounts = components['schemas']['DashboardCountsOut'];
export type DashboardFigures = components['schemas']['DashboardFiguresOut'];
export type DashboardUnit = components['schemas']['DashboardUnitOut'];
export type DashboardGroup = components['schemas']['DashboardGroupOut'];
export type DashboardPreparation = components['schemas']['DashboardPreparationOut'];
export type DashboardAttention = components['schemas']['DashboardAttentionOut'];
export type DashboardCycle = components['schemas']['DashboardCycleOut'];

// --- The reconciliation queue (f14.2) -- Finance/Controller and Platform Admin only, server-side.
export type ReconciliationVariance = components['schemas']['VarianceOut'];
export type ReconciliationVarianceList = components['schemas']['VarianceListResponse'];
export type ReconciliationSummary = components['schemas']['ReconciliationSummaryResponse'];
export type ReconciliationDecision = components['schemas']['DecisionRequest'];
export type ReconciliationQuery = NonNullable<
  operations['listReconciliationVariances']['parameters']['query']
>;

// --- Step 7 "Actuals" (f14.3) and the f14.1 batch endpoints it reuses -- Finance/Admin only.
export type ActualsStepBatches = components['schemas']['ActualsStepBatchesResponse'];
export type ActualsStepBatch = components['schemas']['ActualsStepBatchOut'];
export type ActualsStepExpected = components['schemas']['ActualsStepExpectedOut'];
export type ActualsReview = components['schemas']['ActualsReviewResponse'];
export type ActualsReviewCycle = components['schemas']['ActualsReviewCycleOut'];
export type ActualsStagedRow = components['schemas']['ActualsStagedRowOut'];
export type ActualsStagedRows = components['schemas']['ActualsStagedRowsResponse'];
export type ActualsUploadAccepted = components['schemas']['ActualsUploadAcceptedResponse'];
export type ActualsCommitAccepted = components['schemas']['ActualsCommitAcceptedResponse'];
export type ActualsBatchListItem = components['schemas']['ActualsBatchListItemOut'];

// --- Audit log (f1.5) -- read-only, Platform Admin only, enforced server-side.
export type AuditLogPage = components['schemas']['AuditLogPage'];
export type AuditLogEntrySummary = components['schemas']['AuditLogEntrySummary'];
export type AuditLogEntryDetail = components['schemas']['AuditLogEntryDetail'];
export type AuditEventFamily = components['schemas']['EventFamily'];
export type AuditOutcome = components['schemas']['AuditOutcome'];
export type AuditPayloadShape = components['schemas']['PayloadShape'];
export type AuditActor = components['schemas']['AuditActorResponse'];
export type AuditActorList = components['schemas']['AuditActorListResponse'];
/** The actor list's query parameters: the date range, and nothing else. */
export type AuditActorQuery = NonNullable<
  operations['list_audit_actors_audit_log_actors_get']['parameters']['query']
>;
/** The list operation's query parameters, exactly as the server declares them. */
export type AuditLogQuery = NonNullable<
  operations['list_audit_entries_audit_log_get']['parameters']['query']
>;

// --- Ingestion and background jobs (f8.1) -- Platform Admin only, enforced
// server-side. NOTE what the server does not send, and never will: a job's
// `payload` (it may carry a storage key) and a batch's `storage_key`. Neither
// type below can name them, because the generated schema does not.
export type IngestionBatch = components['schemas']['IngestionBatchResponse'];
export type BatchAccepted = components['schemas']['BatchAcceptedResponse'];
export type IngestionIssueCounts = components['schemas']['IngestionIssueCounts'];
export type Job = components['schemas']['JobResponse'];
export type JobList = components['schemas']['JobListResponse'];
/** The jobs list's query parameters, exactly as the server declares them. */
export type JobQuery = NonNullable<
  operations['list_jobs_jobs_get']['parameters']['query']
>;

// --- The staged import plan (f8.2). Platform Admin only, reads included: a
// staged plan names who owns which client across the whole US organisation,
// and that is the ownership map phase 9 is built on -- not a USER-tier read.
export type IngestionBatchSummary =
  components['schemas']['IngestionBatchSummaryResponse'];
export type IngestionBatchList = components['schemas']['IngestionBatchListResponse'];
export type StagedPlan = components['schemas']['StagedPlanResponse'];
export type StagedNode = components['schemas']['StagedNodeResponse'];
export type StagedLevelCounts = components['schemas']['StagedLevelCountsResponse'];
export type StagedRoleSlot = components['schemas']['StagedRoleSlotResponse'];
export type StagedRoleSlotList = components['schemas']['StagedRoleSlotListResponse'];
export type IngestionIssueSummary =
  components['schemas']['IngestionIssueSummaryResponse'];

// --- Resolving a staged plan, and committing it (f8.3). The decision unit is
// the folded NAME, not the cell and not the slot: one human is one account, so
// `Axel Apice` in fifteen cells across five roles is one question. The server
// carries that folding -- `role_holders` on the role-slots read -- so no
// component regroups 148 rows in the browser.
export type RoleHolderDecision = components['schemas']['RoleHolderDecisionResponse'];
export type NameCandidate = components['schemas']['NameCandidateResponse'];
export type RoleOccupancy = components['schemas']['RoleOccupancyResponse'];
export type ContestedSlot = components['schemas']['ContestedSlotResponse'];
export type ContestedCandidate = components['schemas']['ContestedCandidateResponse'];
export type ResolvedAccount = components['schemas']['ResolvedAccountResponse'];
/** What still stands between the batch and a commit. Counted by the server. */
export type Outstanding = components['schemas']['OutstandingResponse'];
export type RoleHolderResolution =
  components['schemas']['RoleHolderResolutionRequest'];
export type RoleHolderResolved = components['schemas']['RoleHolderResolutionResponse'];
export type ContestedSlotResolved =
  components['schemas']['ContestedSlotResolutionResponse'];
export type StagedNodeCodeResolved = components['schemas']['StagedNodeCodeResponse'];
export type CommitAccepted = components['schemas']['CommitAcceptedResponse'];

/** The batch list's query parameters, exactly as the server declares them. */
export type IngestionBatchQuery = NonNullable<
  operations['list_batches_ingestion_batches_get']['parameters']['query']
>;

// --- Settings (f2.1) -- Platform Admin only, enforced server-side.
export type SettingsCatalog = components['schemas']['SettingsCatalogResponse'];
export type SettingSection = components['schemas']['SettingSectionResponse'];
export type Setting = components['schemas']['SettingResponse'];
export type SettingChange = components['schemas']['SettingChangeRequest'];
export type SettingsUpdate = components['schemas']['SettingsUpdateRequest'];
/** One field-level problem from the error envelope, bound back by `key`. */
export type ErrorItem = components['schemas']['ErrorItem'];

// --- Reference data (f2.2) -- reads for anyone with a role, writes Admin only.
// The lookup TYPES are whatever the server declares: nothing here, and nothing
// in the screen, names `ENTITY_STATUS` or `PLATFORM_ROLE`, so a later phase
// adding a type gets a working tab with no frontend change (BR-F2.2-2).
export type LookupCatalog = components['schemas']['LookupCatalogResponse'];
export type LookupType = components['schemas']['LookupTypeResponse'];
export type LookupValue = components['schemas']['LookupValueResponse'];
export type LookupValueCreate = components['schemas']['LookupValueCreateRequest'];
export type LookupValueUpdate = components['schemas']['LookupValueUpdateRequest'];

export type CountryList = components['schemas']['CountryListResponse'];
export type Country = components['schemas']['CountryResponse'];
export type CountryCreate = components['schemas']['CountryCreateRequest'];
export type CountryUpdate = components['schemas']['CountryUpdateRequest'];

// f10.1: the COM cycle. Screens arrive with f10.2 and f10.3.
export type Cycle = components['schemas']['CycleResponse'];
/* f12.1: the review screen (the FS sheet). */
export type Review = components['schemas']['ReviewResponse'];
export type ReviewRow = components['schemas']['ReviewRowOut'];
export type ReviewFigures = components['schemas']['ReviewFiguresOut'];
/* f12.2: the live stream's two event shapes (consumed by `useLiveCycle`). */
export type LiveFiguresEvent = components['schemas']['LiveFiguresEvent'];
export type LiveClosedEvent = components['schemas']['LiveClosedEvent'];
export type ReviewUnitSection = components['schemas']['ReviewUnitSectionOut'];
export type ReviewTotals = components['schemas']['ReviewTotalsOut'];
export type ReviewUnits = components['schemas']['ReviewUnitsResponse'];
export type ReviewRowSaved = components['schemas']['ReviewRowEnvelope'];
export type ReviewCommentThread = components['schemas']['CommentThreadResponse'];
export type ReviewCommentItem = components['schemas']['CommentThreadItemOut'];
export type ReviewRowChange = NonNullable<
  operations['updateReviewRow']['requestBody']
>['content']['application/json'];
/* f11.1: the forecast grid. */
export type ForecastGrid = components['schemas']['ForecastGridResponse'];
export type ForecastLine = components['schemas']['ForecastLineResponse'];
export type ForecastLineMonth = components['schemas']['LineMonthResponse'];
export type ForecastGridUnits = components['schemas']['GridUnitsResponse'];
/* f11.3: what the cycle rail offers the caller for forecast input. */
export type CycleInputEntry = components['schemas']['InputEntryResponse'];
export type CycleInputEntryUnit = components['schemas']['InputEntryUnit'];
/* f11.4: entry route B, the Commitments tab on Client detail. */
export type ClientCommitments = components['schemas']['ClientCommitmentsResponse'];
export type ClientCycleCommitment = components['schemas']['ClientCycleCommitmentResponse'];
export type CommitmentCurrencyFigures = components['schemas']['CurrencyFiguresResponse'];
export type ForecastLineUpdated = components['schemas']['ForecastLineUpdateResponse'];
export type ForecastMoney = components['schemas']['MoneyResponse'];
export type EngineWarning = components['schemas']['EngineWarningResponse'];
/** f11.6: a non-blocking input check on a 200 body -- never an error. */
export type ValidationWarning = components['schemas']['ValidationWarningOut'];
export type ValidationWarningCode = ValidationWarning['code'];
export type ValidationSummary = components['schemas']['ValidationSummaryOut'];
export type CycleValidationWarnings = components['schemas']['CycleValidationWarningsResponse'];
/* f11.2: band 2, add a confirmed-master element to a cycle. */
export type ElementCandidates = components['schemas']['ElementCandidatesResponse'];
export type ElementCandidate = components['schemas']['ElementCandidateResponse'];
export type ForecastElementAdded = components['schemas']['ForecastElementAddedResponse'];
export type AddForecastElementBody = operations['addForecastElement']['requestBody']['content']['application/json'];
/** The PATCH body; every field but `expected_version` optional. */
export type ForecastLineChange = NonNullable<
  operations['updateForecastLine']['requestBody']
>['content']['application/json'];
export type CycleTransition = components['schemas']['CycleTransitionRequest'];
export type CycleReopen = components['schemas']['CycleReopenRequest'];
export type CycleVersionConflict = components['schemas']['CycleVersionConflictResponse'];
// f10.2: the list, the preview and the create. Every derived value -- label,
// month windows, previous cycle, working-day dates -- comes from the server.
export type CycleList = components['schemas']['CycleListResponse'];
export type CycleListItem = components['schemas']['CycleListItem'];
export type CycleDerived = components['schemas']['CycleDerivedOut'];
export type CycleMilestone = components['schemas']['CycleMilestoneOut'];
export type CyclePreview = components['schemas']['CyclePreviewResponse'];
export type CreateCycle = components['schemas']['CreateCycleRequest'];
export type CycleListQuery = { state?: string; year?: number };
// f10.3: the preparation step rail. Status, completion, staleness and every
// capability (`can_accept`, `can_apply_update`) are computed by the server.
export type CycleWorkflow = components['schemas']['WorkflowResponse'];
export type CycleWorkflowStep = components['schemas']['WorkflowStepOut'];
export type StepChange = components['schemas']['StepChangeRequest'];
// f10.9: step 6, release to managers. Readiness, `can_release` and the missing
// list are the server's; the release recomputes them in its own transaction.
export type ReleaseReadiness = components['schemas']['ReleaseReadinessResponse'];
export type ReleaseReadinessStep = components['schemas']['ReleaseStepOut'];
export type ReleaseCycle = components['schemas']['ReleaseRequest'];
export type ReleaseCycleResult = components['schemas']['ReleaseResponse'];
export type ApplyAccepted = components['schemas']['ApplyAcceptedResponse'];
// f12.3: the Finance-only lock. The preview's blockers and `can_freeze` are the
// server's; the freeze recomputes them in its own transaction.
export type FreezePreview = components['schemas']['FreezePreviewResponse'];
export type FreezeBlocker = components['schemas']['FreezeBlockerOut'];
export type FreezeCycle = components['schemas']['FreezeCycleRequest'];
export type CycleSnapshotRef = components['schemas']['CycleSnapshotRefResponse'];
export type CycleSnapshotList = components['schemas']['CycleSnapshotListResponse'];
export type CycleSnapshot = components['schemas']['CycleSnapshotItemOut'];
// f13.1: the scoped cycle CSV export -- a job the requester polls, then downloads.
export type CycleExportAccepted = components['schemas']['CycleExportAcceptedResponse'];
export type CycleExportStatus = components['schemas']['CycleExportStatusResponse'];
// f13.2: the Finance-only submission (Frozen -> Submitted) and its recorded file.
export type SubmitCycle = components['schemas']['SubmitCycleRequest'];
export type SubmitCycleResult = components['schemas']['SubmitCycleResponse'];
export type CycleSubmission = components['schemas']['SubmissionRecordResponse'];
// f10.4: step 1, the US Engagements master. Finance/Admin only, enforced by
// the server. Matching, decision rules, figures and every capability are the
// server's; the screen renders them and never re-derives one.
export type UsEngagementsUploadAccepted = components['schemas']['UploadAcceptedResponse'];
export type UsEngagementsBatch = components['schemas']['BatchOut'];
export type UsEngagementsBatchList = components['schemas']['BatchListResponse'];
export type UsEngagementsReview = components['schemas']['BatchReviewResponse'];
export type UsEngagementsAccount = components['schemas']['AccountItemOut'];
export type UsEngagementsEngagement = components['schemas']['EngagementItemOut'];
export type UsEngagementsElement = components['schemas']['ElementItemOut'];
export type UsEngagementsFigures = components['schemas']['FiguresOut'];
export type UsEngagementsIssue = components['schemas']['IssueOut'];
export type UsEngagementsDecisionResult = components['schemas']['DecisionResponse'];
export type UsEngagementsCommitAccepted = components['schemas']['StepCommitAcceptedResponse'];
export type AccountDecisionBody = components['schemas']['AccountDecisionRequest'];
export type EngagementDecisionBody = components['schemas']['EngagementDecisionRequest'];
export type ElementDecisionBody = components['schemas']['ElementDecisionRequest'];
export type BulkDecisionBody = components['schemas']['BulkDecisionRequest'];
/** The three item collections of a step-1 batch, as they appear in the URL. */
export type UsEngagementsItemKind = 'accounts' | 'engagements' | 'elements';
// f10.5: step 2, billable days. The batch envelope and acknowledgements are
// step 1's shapes; figures, resolution and every capability are the server's.
export type BillableDaysReview = components['schemas']['BillableDaysReviewResponse'];
export type BillableDaysFigures = components['schemas']['BillableDaysFiguresOut'];
export type BillableDaysCountryItem = components['schemas']['CountryItemOut'];
export type BillableDaysDecisionBody = components['schemas']['CountryDecisionRequest'];
export type BillableDaysDecisionResult = components['schemas']['CountryDecisionResponse'];
// f10.10: budget reference data -- Finance/Controller and Platform Admin only,
// reads included, enforced server-side. Every amount is a decimal STRING.
export type ClientAnnualBudgetList = components['schemas']['ClientAnnualListResponse'];
export type ClientAnnualBudgetItem = components['schemas']['ClientAnnualItemOut'];
export type ClientAnnualBudgetSave = components['schemas']['ClientAnnualRequest'];
export type UnitMonthlyBudgetList = components['schemas']['UnitMonthlyListResponse'];
export type UnitMonthlyBudgetItem = components['schemas']['UnitMonthlyItemOut'];
export type UnitMonthlyBudgetSave = components['schemas']['UnitMonthlyRequest'];
export type BudgetMoney = components['schemas']['MoneyOut'];
export type BudgetImportAccepted = components['schemas']['ImportAcceptedResponse'];
export type BudgetImportCommitAccepted = components['schemas']['ImportCommitAcceptedResponse'];
export type BudgetImportList = components['schemas']['ImportBatchListResponse'];
export type BudgetImportBatch = components['schemas']['ImportBatchOut'];
export type BudgetImportReview = components['schemas']['ImportReviewResponse'];
export type BudgetImportKind = 'CLIENT_ANNUAL' | 'UNIT_MONTHLY';
/** What `409 VERSION_CONFLICT` carries: the figure as it stands now. */
export interface BudgetCurrent {
  amount: string;
  currency_code: string;
  version: number;
  updated_at: string | null;
  updated_by: string | null;
  month?: number;
}

// f10.8: step 5, exchange rates. The batch envelope and acknowledgements are
// step 1's shapes; every rate is a decimal STRING, rendered verbatim.
export type ExchangeRatesReview = components['schemas']['ExchangeRatesReviewResponse'];
export type ExchangeRateRow = components['schemas']['FxRowOut'];
export type ExchangeRatesFigures = components['schemas']['FxFiguresOut'];
export type CycleExchangeRates = components['schemas']['CycleExchangeRatesResponse'];
// f10.6: step 3, the three Kimble dumps. Figures, resolution and every
// capability are the server's; the scope list is lookup data.
export type KimbleScopes = components['schemas']['KimbleScopesResponse'];
export type KimbleScope = components['schemas']['KimbleScopeOut'];
export type KimbleBatch = components['schemas']['KimbleBatchOut'];
export type KimbleBatchList = components['schemas']['KimbleBatchListResponse'];
export type KimbleReview = components['schemas']['KimbleReviewResponse'];
export type KimbleFigures = components['schemas']['KimbleFiguresOut'];
export type KimbleResourceBuItem = components['schemas']['ResourceBuItemOut'];
export type KimbleUnitItem = components['schemas']['UnitItemOut'];
export type KimbleResourceBuDecisionBody = components['schemas']['ResourceBuDecisionRequest'];
export type KimbleUnitDecisionBody = components['schemas']['UnitDecisionRequest'];
export type KimbleResourceBuDecisionResult = components['schemas']['ResourceBuDecisionResponse'];
export type KimbleUnitDecisionResult = components['schemas']['UnitDecisionResponse'];

/* f10.7: step 4, the three COSMA sales-rate reports. Resolution, keys, figures
   and every capability come from the server; a null rate is never zero. */
export type SalesRateScopes = components['schemas']['SalesRateScopesResponse'];
export type SalesRateScope = components['schemas']['SalesRateScopeOut'];
export type SalesRateBatch = components['schemas']['SalesRateBatchOut'];
export type SalesRateBatchList = components['schemas']['SalesRateBatchListResponse'];
export type SalesRateReview = components['schemas']['SalesRateReviewResponse'];
export type SalesRateFigures = components['schemas']['SalesRateFiguresOut'];
export type SalesRateElement = components['schemas']['SalesRateElementOut'];
export type RateCell = components['schemas']['RateCellOut'];
export type SalesRateBuDecisionBody = components['schemas']['SalesRateBuDecisionRequest'];
export type SalesRateElementDecisionBody = components['schemas']['SalesRateElementDecisionRequest'];
export type SalesRateDecideAllBody = components['schemas']['SalesRateDecideAllRequest'];
export type SalesRateBuDecisionResult = components['schemas']['SalesRateBuDecisionResponse'];
export type SalesRateElementDecisionResult = components['schemas']['SalesRateElementDecisionResponse'];
export type SalesRateDecideAllResult = components['schemas']['SalesRateDecideAllResponse'];

export type ResourceBuMappingList = components['schemas']['ResourceBuMappingListResponse'];
export type ResourceBuMapping = components['schemas']['ResourceBuMappingResponse'];
export type ResourceBuMappingCreate = components['schemas']['ResourceBuMappingCreateRequest'];
export type ResourceBuMappingUpdate = components['schemas']['ResourceBuMappingUpdateRequest'];
/** A referenced row expanded by the server, so no client joins. */
export type ReferenceLabel = components['schemas']['ReferenceLabelResponse'];

// --- Users (f3.1) -- Platform Admin only, reads included, enforced server-side.
// Nothing here names a role code. The grantable vocabulary is f2.2's
// PLATFORM_ROLE lookup, read at runtime, so a role inserted in phase 10 or 16
// becomes grantable with no change to this file (BR-9).
export type UserAccount = components['schemas']['UserAccountResponse'];
export type UserList = components['schemas']['UserListResponse'];
export type UserUpdate = components['schemas']['UserUpdateRequest'];
export type UserInvite = components['schemas']['UserInviteRequest'];
/**
 * `active` | `awaiting_role` | `deactivated` | `invited`.
 *
 * Computed by the SERVER, never here. `invited` (f3.2) is why that rule is
 * worth keeping: it derives from `entra_oid IS NULL`, which the client never
 * sees, and it does NOT imply an inactive account -- so any browser-side
 * derivation would be quietly wrong rather than loudly missing.
 */
export type UserStatus = components['schemas']['UserStatus'];

// --- Organization Overview: the whole scoped hierarchy in one read.
export type OrganizationOverview = components['schemas']['OrganizationOverviewResponse'];
export type OverviewGroup = components['schemas']['OverviewGroupResponse'];
export type OverviewUnit = components['schemas']['OverviewUnitResponse'];
export type OverviewClient = components['schemas']['OverviewClientResponse'];
export type OverviewEngagement = components['schemas']['OverviewEngagementResponse'];
export type OverviewCounts = components['schemas']['OverviewCountsResponse'];

// --- Business Unit Groups (f4.1) -- reads for anyone in scope, writes restricted.
// Nothing here names a role code: whether the write controls render is decided
// from the session's `permissions`, which the server computes from the same
// tuple the write routes enforce.
export type BusinessUnitGroup = components['schemas']['BusinessUnitGroupResponse'];
export type BusinessUnitGroupList = components['schemas']['BusinessUnitGroupListResponse'];
export type BusinessUnitGroupCreate = components['schemas']['BusinessUnitGroupCreateRequest'];
export type BusinessUnitGroupUpdate = components['schemas']['BusinessUnitGroupUpdateRequest'];
export type BusinessUnitGroupDeactivate =
  components['schemas']['BusinessUnitGroupDeactivateRequest'];
/** Who made a change, as the audit log recorded them AT THE TIME. */
export type Actor = components['schemas']['ActorResponse'];
/** The children a refused deactivation named, so the dialog lists them. */
export type ActiveChildren = components['schemas']['ActiveChildrenBlock'];

/*
 * Business Units (f5.1) -- the second hierarchy level, and the unit of COM
 * data entry. Reads for anyone in scope, writes behind the SAME shared
 * hierarchy policy f4.1 declared; nothing here names a role code.
 *
 * Six operations and no seventh. There is no delete to call: the server
 * exposes none, and could not perform one if it did, because the runtime role
 * holds no DELETE privilege on `business_unit`. Status moves through the two
 * transition calls, never through the PATCH, and the parent group is fixed at
 * create -- neither `code` nor `business_unit_group_id` is accepted on an edit.
 */
export type BusinessUnit = components['schemas']['BusinessUnitResponse'];
export type BusinessUnitList = components['schemas']['BusinessUnitListResponse'];
export type BusinessUnitCreate = components['schemas']['BusinessUnitCreateRequest'];
export type BusinessUnitUpdate = components['schemas']['BusinessUnitUpdateRequest'];
export type BusinessUnitDeactivate =
  components['schemas']['BusinessUnitDeactivateRequest'];
/**
 * A unit's reporting currency: `{code, name}` and no id.
 *
 * Not a `ReferenceLabel`, and that is the server's shape rather than an
 * omission: `business_unit.reporting_currency_code` is a foreign key to
 * `currency(code)`, so the code IS the reference and no row stores an id a
 * client could join on. The NAME still comes from the server, so no component
 * holds a map from `USD` to `US Dollar`.
 */
export type Currency = components['schemas']['CurrencyResponse'];

/*
 * Clients (f6.1) -- the third hierarchy level, and the hub three later phases
 * open. Reads for anyone in scope, writes behind the SAME shared hierarchy
 * policy f4.1 declared; nothing here names a role code.
 *
 * Six operations and no seventh. There is no delete to call: the server
 * exposes none, and could not perform one if it did, because the runtime role
 * holds no DELETE privilege on `client`. Status moves through the two
 * transition calls, never through the PATCH, and the parent unit is fixed at
 * create -- neither `code` nor `business_unit_id` is accepted on an edit.
 *
 * The collection takes NO parameter of any kind. 39 rows: the page controls,
 * the search box and the four selects all run client-side.
 */
export type Client = components['schemas']['ClientResponse'];
export type ClientList = components['schemas']['ClientListResponse'];
export type ClientCreate = components['schemas']['ClientCreateRequest'];
export type ClientUpdate = components['schemas']['ClientUpdateRequest'];
export type ClientDeactivate = components['schemas']['ClientDeactivateRequest'];
/**
 * One owner of an ANCESTOR of a client, for display beside its own DM.
 *
 * A different shape from `CurrentOwner` because it carries the node the
 * ownership comes FROM -- the card renders `CM · CU US Asset 01`, and a reader
 * has to be able to see that the row is not this client's own.
 *
 * **Never an authorization input.** It shows who is accountable; it decides
 * nothing, and f9.1 owns the scope walk-down that does.
 */
export type InheritedOwner = components['schemas']['InheritedOwnerResponse'];

/*
 * Engagements (f7.1) -- the fourth and last hierarchy level, and the first
 * with NO screen and NO navigation entry. An engagement only has meaning
 * inside a client, so it is a TAB inside Client detail.
 *
 * Ten operations and no eleventh. There is no delete to call, and could not
 * be: the server exposes none and the runtime role holds no DELETE privilege
 * on either table. There is also **no create for an element** -- delivery
 * elements are discovered from the Kimble feed, and f10.4 owns the insert.
 *
 * There is **no `getEngagements()`**: the collection hangs off its client,
 * because that is what the tab renders and no screen lists engagements across
 * clients. No read takes a parameter of any kind -- the largest client has
 * seven engagements and the largest engagement 28 elements.
 */
export type Engagement = components['schemas']['EngagementResponse'];
export type EngagementList = components['schemas']['EngagementListResponse'];
export type EngagementCreate = components['schemas']['EngagementCreateRequest'];
export type EngagementUpdate = components['schemas']['EngagementUpdateRequest'];
export type EngagementDeactivate =
  components['schemas']['EngagementDeactivateRequest'];
/**
 * One delivery element, as adr-17's row expansion renders it.
 *
 * `name` is normally null and that is NOT a gap in the data load: no source in
 * the estate carries an element name. It renders through the shared empty
 * cell, never as a fabricated label and never as the engagement's name.
 */
export type EngagementElement =
  components['schemas']['EngagementElementResponse'];
export type EngagementElementList =
  components['schemas']['EngagementElementListResponse'];
export type ElementDeactivate = components['schemas']['ElementDeactivateRequest'];

/*
 * Delivery ownership (f4.2) -- reads for anyone in scope, writes restricted.
 *
 * Nothing here names a delivery-role code: the vocabulary is f2.2's
 * DELIVERY_ROLE lookup, read at runtime through the SAME lookup endpoint every
 * other type uses, so a sixth role added in a later phase needs no change here.
 */
/**
 * An account the assign dialog may name as an owner (c-strict).
 *
 * There is no roster type beside this one: ADR-19's `person` table is gone, so
 * a delivery-role holder IS an Atlas account. The list leaves DEACTIVATED
 * accounts out and keeps `invited` and `awaiting_role` in -- owning a node is
 * not a permission grant, and pre-provisioning somebody's account is how their
 * DEM or CEM is recorded before their first sign-in.
 *
 * `status` is f3.1's own `UserStatus`, reused rather than narrowed to the three
 * values this operation can emit: the Users screen already renders that
 * vocabulary, and a second enum meaning the same thing is how two screens come
 * to disagree. The type therefore admits `deactivated` even though the
 * operation can never return it.
 */
export type AssignableUser = components['schemas']['AssignableUserResponse'];
export type AssignableUserList = components['schemas']['AssignableUserListResponse'];
/** Who currently holds a role on a node -- `DEM` and `CEM` on a group. */
export type CurrentOwner = components['schemas']['CurrentOwnerResponse'];
export type DeliveryRoleAssignment =
  components['schemas']['DeliveryRoleAssignmentResponse'];
export type DeliveryRoleAssignmentList =
  components['schemas']['DeliveryRoleAssignmentListResponse'];
export type DeliveryRoleAssignmentCreate =
  components['schemas']['DeliveryRoleAssignmentCreateRequest'];
/** The holder a `409 OWNER_ALREADY_ASSIGNED` named, so the dialog can replace them. */
export type CurrentHolder = components['schemas']['CurrentHolderBlock'];

/**
 * The caller's situation, decided by the SERVER.
 *
 * The shell branches on this and never on `role.code`. Deriving the state
 * from a role code in the browser would be an authorization decision taken
 * on the client -- and it has no answer at all for `deactivated`, which is not
 * a role but a state of the account.
 */
export type AccessState = components['schemas']['AccessState'];

/** An unsuccessful response, carrying enough to render and to branch on. */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  /** The envelope's enumerated `reason`, when the failure carried one. */
  readonly reason: string | null;
  /** The envelope's per-field problems; empty for anything else. */
  readonly errors: readonly ErrorItem[];
  /**
   * The children a refused deactivation named (f4.1).
   *
   * Present only on `409 ACTIVE_CHILDREN_PRESENT`. The client NEVER computes
   * this list: the dialog renders what the server refused, so the warning
   * cannot disagree with the rule that produced it.
   */
  readonly activeChildren: ActiveChildren | null;
  /**
   * Who currently holds the role (f4.2).
   *
   * Present only on `409 OWNER_ALREADY_ASSIGNED`. Replacing them is end then
   * add -- two requests, both audited, and the ended one is preserved.
   */
  readonly currentHolder: CurrentHolder | null;
  /**
   * The figure as it stands now (f10.10). Present only on
   * `409 VERSION_CONFLICT`, so the screen can show it and let the user
   * re-apply -- adr-14's mutation-error pattern.
   */
  readonly current: BudgetCurrent | null;
  /**
   * The raw error body, for the refusals that carry more than the envelope
   * (f11.1's `409 FORECAST_LINE_VERSION_CONFLICT` carries the current line).
   */
  readonly body: unknown;

  constructor(
    status: number,
    url: string,
    message: string,
    details: {
      reason?: string | null;
      errors?: readonly ErrorItem[] | null;
      activeChildren?: ActiveChildren | null;
      currentHolder?: CurrentHolder | null;
      current?: BudgetCurrent | null;
      body?: unknown;
    } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.reason = details.reason ?? null;
    this.errors = details.errors ?? [];
    this.activeChildren = details.activeChildren ?? null;
    this.currentHolder = details.currentHolder ?? null;
    this.current = details.current ?? null;
    this.body = details.body ?? null;
  }
}

/** f17.3: sent on every API call, so writes pass the server's CSRF check. */
export const CSRF_HEADER = 'X-Requested-With';

/** The URL of an API path (relative when the base is, e.g. `/api/...`). f12.2's stream reader uses it; everything else goes through `request`. */
export function apiUrl(path: string): string {
  return url(path);
}

function url(path: string): string {
  // Tolerate a trailing slash on the configured base so that
  // "http://host:8010/" and "http://host:8010" behave identically.
  return `${appConfig.apiBaseUrl.replace(/\/+$/, '')}${path}`;
}

/**
 * A query string from a flat parameter object.
 *
 * Absent, null and empty values are dropped rather than sent as `?x=` -- an
 * empty filter is "no filter", and omitting it keeps the URL honest.
 */
export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly (string | number)[];

/**
 * Query parameters, with a repeated key per element of an array.
 *
 * `{ actor_user_id: ['a', 'b'] }` becomes `?actor_user_id=a&actor_user_id=b`,
 * which is how FastAPI reads a `list[...]` parameter. A comma-joined value
 * would arrive as one string containing a comma and be refused.
 */
export function queryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
      continue;
    }
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

interface Failure {
  message: string;
  reason: string | null;
  errors: readonly ErrorItem[];
  activeChildren: ActiveChildren | null;
  currentHolder: CurrentHolder | null;
  current: BudgetCurrent | null;
  body: unknown;
}

function isBudgetCurrent(value: unknown): value is BudgetCurrent {
  if (typeof value !== 'object' || value === null) return false;
  const block = value as Record<string, unknown>;
  return typeof block.amount === 'string' && typeof block.version === 'number';
}

function isActiveChildren(value: unknown): value is ActiveChildren {
  if (typeof value !== 'object' || value === null) return false;
  const block = value as Record<string, unknown>;
  return typeof block.total === 'number' && Array.isArray(block.listed);
}

function isCurrentHolder(value: unknown): value is CurrentHolder {
  if (typeof value !== 'object' || value === null) return false;
  const block = value as Record<string, unknown>;
  return (
    typeof block.assignment_id === 'string' &&
    typeof block.user_id === 'string' &&
    typeof block.display_name === 'string' &&
    typeof block.delivery_role_code === 'string'
  );
}

function isErrorItem(value: unknown): value is ErrorItem {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.key === 'string' &&
    typeof item.code === 'string' &&
    typeof item.message === 'string'
  );
}

/**
 * The server's own explanation of a refusal, when it gave one.
 *
 * Every Atlas error is the one envelope `{message, reason, errors}`, whichever
 * route answered: `reason` is the enumerated code for a failure of the request
 * as a whole, and `errors` binds field-level problems back to form controls by
 * key. One shape, so one branch here. Anything that is not that shape -- a body
 * that is not JSON, or an error from something in front of the API such as a
 * proxy -- falls back to the status.
 */
async function failureFrom(response: Response): Promise<Failure> {
  const fallback = `Request failed with status ${response.status}.`;
  try {
    const body = (await response.json()) as Record<string, unknown> | null;
    if (body && typeof body.message === 'string' && body.message.trim()) {
      return {
        message: body.message,
        reason: typeof body.reason === 'string' ? body.reason : null,
        errors: Array.isArray(body.errors) ? body.errors.filter(isErrorItem) : [],
        // A strict SUPERSET of the envelope, never a different shape: absent
        // on every refusal but the one that carries it.
        activeChildren: isActiveChildren(body.active_children)
          ? body.active_children
          : null,
        currentHolder: isCurrentHolder(body.current_holder)
          ? body.current_holder
          : null,
        current: isBudgetCurrent(body.current) ? body.current : null,
        body,
      };
    }
  } catch {
    /* Not JSON; the status is all there is. */
  }
  return {
    message: fallback,
    reason: null,
    errors: [],
    activeChildren: null,
    currentHolder: null,
    current: null,
    body: null,
  };
}

interface RequestOptions extends RequestInit {
  /** Anonymous requests (the session probe before sign-in) set this. */
  allowAnonymous?: boolean;
}

/**
 * Send a request and return the successful `Response`. `request` (JSON) and
 * `requestBlob` (a file) both come through here, so a download cannot grow a
 * second path that forgets the CSRF header or the 401 handling.
 */
async function send(path: string, options: RequestOptions = {}, accept = 'application/json'): Promise<Response> {
  if (!appConfig.isConfigured) {
    // Fail with the actual cause rather than firing a request at the
    // frontend's own origin, which nginx would answer with index.html and
    // which would then surface as an inscrutable JSON parse error.
    throw new ApiError(
      0,
      path,
      'Atlas has no API base URL configured. Set PUBLIC_API_URL in the ' +
        'repository-root .env: in a container the entrypoint writes it into ' +
        'config.js, and local vite dev reads the same key directly.',
    );
  }

  const { allowAnonymous = false, ...init } = options;
  const target = url(path);
  const headers = new Headers(init.headers);
  headers.set('Accept', accept);
  // D6: the API refuses a write without it. A cross-site page cannot add a
  // custom header without a preflight the API never grants.
  headers.set(CSRF_HEADER, 'Atlas');

  let response: Response;
  try {
    response = await fetch(target, { ...init, headers, credentials: 'same-origin' });
  } catch {
    // A network-level failure has no status. Surface it as one shape rather
    // than letting a raw TypeError reach a component.
    throw new ApiError(0, target, 'The Atlas API could not be reached.');
  }

  if (response.status === 401 && !allowAnonymous) {
    // The session ended. One trip to the sign-in screen, however many
    // requests fail together.
    returnToSignIn();
    throw new ApiError(401, target, 'The session is no longer valid.');
  }

  if (!response.ok) {
    const failure = await failureFrom(response);
    throw new ApiError(response.status, target, failure.message, failure);
  }

  return response;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  const target = response.url || url(path);

  if (response.status === 204) {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(
      response.status,
      target,
      'The API returned a response that was not valid JSON.',
    );
  }
}

/** A file body (f13.1's CSV download), through the same authorised `send`. */
async function requestBlob(path: string, accept: string): Promise<Blob> {
  const response = await send(path, {}, accept);
  return response.blob();
}

/** Where each tab-carrying node lists its artifacts (f15.1). */
const ARTIFACT_OWNER_BASES: Record<ArtifactListOwnerType, string> = {
  BUSINESS_UNIT_GROUP: '/business-unit-groups',
  BUSINESS_UNIT: '/business-units',
  CLIENT: '/clients',
};

export const api = {
  // Anonymous-tolerant: the shell must be able to ask who it is before a
  // session exists, in order to render the sign-in screen.
  getSession: () => request<SessionResponse>('/session', { allowAnonymous: true }),
  getHealth: () => request<HealthResponse>('/health', { allowAnonymous: true }),

  getMe: () => request<UserResponse>('/auth/me'),
  signOut: () => request<void>('/auth/logout', { method: 'POST' }),

  // The audit log: two reads and nothing else. There is no write, export or
  // purge operation to call -- the server exposes none.
  listAuditLog: (query: AuditLogQuery) =>
    request<AuditLogPage>(`/audit-log${queryString(query)}`),
  getAuditLogEntry: (entryId: string) =>
    request<AuditLogEntryDetail>(`/audit-log/${encodeURIComponent(entryId)}`),
  /* The actors a reader may filter by, for one date range. Read from the log
     rather than from the roster, so a background job and a failed sign-in
     both appear -- neither has an account to look up. */
  listAuditActors: (query: AuditActorQuery) =>
    request<AuditActorList>(`/audit-log/actors${queryString(query)}`),

  // Settings (f2.1): the whole catalogue, and a batch of changes applied all
  // or nothing. There is no create or delete operation to call -- the server
  // exposes none, and could not perform one.
  getSettings: () => request<SettingsCatalog>('/settings'),
  updateSettings: (update: SettingsUpdate) =>
    request<SettingsCatalog>('/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    }),

  // Reference data (f2.2). Three collections, none of them paginated: there
  // is no cursor to pass and no `has_more` to follow, because reference data
  // is bounded by human maintenance. No delete operation exists to call -- a
  // value is retired by clearing its active flag.
  getLookups: () => request<LookupCatalog>('/lookups'),
  addLookupValue: (typeCode: string, value: LookupValueCreate) =>
    request<LookupValue>(`/lookups/${encodeURIComponent(typeCode)}/values`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(value),
    }),
  updateLookupValue: (typeCode: string, valueId: string, change: LookupValueUpdate) =>
    request<LookupValue>(
      `/lookups/${encodeURIComponent(typeCode)}/values/${encodeURIComponent(valueId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(change),
      },
    ),

  getCountries: () => request<CountryList>('/countries'),
  addCountry: (country: CountryCreate) =>
    request<Country>('/countries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(country),
    }),
  updateCountry: (countryId: string, change: CountryUpdate) =>
    request<Country>(`/countries/${encodeURIComponent(countryId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),

  // Users (f3.1, f3.2): the roster, the invite, and one PATCH for every
  // administrative change. There is still no delete, no export, no resend and
  // no accept-invite operation to call -- the server exposes none. An account is
  // created by signing in (f1.3) or by invitation (f3.2), cancelling an invite
  // is a PATCH setting `is_active` false, and deactivation is the only removal.
  getUsers: () => request<UserList>('/users'),
  inviteUser: (invite: UserInvite) =>
    request<UserAccount>('/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invite),
    }),
  updateUser: (userId: string, change: UserUpdate) =>
    request<UserAccount>(`/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),

  getResourceBuMappings: () => request<ResourceBuMappingList>('/resource-bu-mappings'),
  addResourceBuMapping: (mapping: ResourceBuMappingCreate) =>
    request<ResourceBuMapping>('/resource-bu-mappings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapping),
    }),
  updateResourceBuMapping: (mappingId: string, change: ResourceBuMappingUpdate) =>
    request<ResourceBuMapping>(`/resource-bu-mappings/${encodeURIComponent(mappingId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),

  // The Organization Overview: one unpaginated, scoped read for all four layouts.
  getOrganizationOverview: () => request<OrganizationOverview>('/organization/overview'),

  // Business Unit Groups (f4.1): six operations and no seventh. There is no
  // delete operation to call -- the server exposes none, and could not perform
  // one if it did: the runtime role holds no DELETE on the table. Status moves
  // through the two transition calls, never through the PATCH.
  getBusinessUnitGroups: () => request<BusinessUnitGroupList>('/business-unit-groups'),
  getBusinessUnitGroup: (groupId: string) =>
    request<BusinessUnitGroup>(`/business-unit-groups/${encodeURIComponent(groupId)}`),
  addBusinessUnitGroup: (group: BusinessUnitGroupCreate) =>
    request<BusinessUnitGroup>('/business-unit-groups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(group),
    }),
  updateBusinessUnitGroup: (groupId: string, change: BusinessUnitGroupUpdate) =>
    request<BusinessUnitGroup>(`/business-unit-groups/${encodeURIComponent(groupId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),
  deactivateBusinessUnitGroup: (groupId: string, body: BusinessUnitGroupDeactivate) =>
    request<BusinessUnitGroup>(
      `/business-unit-groups/${encodeURIComponent(groupId)}/deactivate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  // Business Units (f5.1): the same six operations as the level above, and
  // the same absence of a seventh. No delete exists to call.
  getBusinessUnits: () => request<BusinessUnitList>('/business-units'),
  getBusinessUnit: (unitId: string) =>
    request<BusinessUnit>(`/business-units/${encodeURIComponent(unitId)}`),
  addBusinessUnit: (unit: BusinessUnitCreate) =>
    request<BusinessUnit>('/business-units', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(unit),
    }),
  updateBusinessUnit: (unitId: string, change: BusinessUnitUpdate) =>
    request<BusinessUnit>(`/business-units/${encodeURIComponent(unitId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),
  deactivateBusinessUnit: (unitId: string, body: BusinessUnitDeactivate) =>
    request<BusinessUnit>(
      `/business-units/${encodeURIComponent(unitId)}/deactivate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  reactivateBusinessUnit: (unitId: string) =>
    request<BusinessUnit>(
      `/business-units/${encodeURIComponent(unitId)}/reactivate`,
      { method: 'POST' },
    ),

  /*
   * Clients (f6.1): the same six operations as the two levels above, and the
   * same absence of a seventh. No delete exists to call, and no read takes a
   * parameter of any kind.
   */
  getClients: () => request<ClientList>('/clients'),
  getClient: (clientId: string) =>
    request<Client>(`/clients/${encodeURIComponent(clientId)}`),
  addClient: (client: ClientCreate) =>
    request<Client>('/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(client),
    }),
  updateClient: (clientId: string, change: ClientUpdate) =>
    request<Client>(`/clients/${encodeURIComponent(clientId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),
  deactivateClient: (clientId: string, body: ClientDeactivate) =>
    request<Client>(`/clients/${encodeURIComponent(clientId)}/deactivate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  reactivateClient: (clientId: string) =>
    request<Client>(`/clients/${encodeURIComponent(clientId)}/reactivate`, {
      method: 'POST',
    }),

  /*
   * Engagements (f7.1). Ten operations and no eleventh: no delete on either
   * table, and no create for an element.
   *
   * The collection is addressed through its CLIENT, because that is what the
   * tab renders -- there is deliberately no `/engagements` collection for any
   * screen to read.
   */
  /* f11.4: the client's forecast cycles for the caller (read only). */
  getClientCommitments: (clientId: string) =>
    request<ClientCommitments>(`/clients/${encodeURIComponent(clientId)}/commitments`),
  getClientEngagements: (clientId: string) =>
    request<EngagementList>(
      `/clients/${encodeURIComponent(clientId)}/engagements`,
    ),
  getEngagement: (engagementId: string) =>
    request<Engagement>(`/engagements/${encodeURIComponent(engagementId)}`),
  addEngagement: (engagement: EngagementCreate) =>
    request<Engagement>('/engagements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(engagement),
    }),
  updateEngagement: (engagementId: string, change: EngagementUpdate) =>
    request<Engagement>(`/engagements/${encodeURIComponent(engagementId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    }),
  deactivateEngagement: (engagementId: string, body: EngagementDeactivate) =>
    request<Engagement>(
      `/engagements/${encodeURIComponent(engagementId)}/deactivate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  reactivateEngagement: (engagementId: string) =>
    request<Engagement>(
      `/engagements/${encodeURIComponent(engagementId)}/reactivate`,
      { method: 'POST' },
    ),

  /*
   * The elements of ONE engagement, fetched when a row is expanded rather
   * than embedded in the collection: 79 of 141 engagements have exactly one
   * element, so embedding would make the common case pay for the rare one.
   *
   * There is no `addElement`. Elements are discovered from the Kimble feed,
   * and f10.4 owns the insert.
   */
  getEngagementElements: (engagementId: string) =>
    request<EngagementElementList>(
      `/engagements/${encodeURIComponent(engagementId)}/elements`,
    ),
  confirmElement: (engagementId: string, elementId: string) =>
    request<EngagementElement>(
      `/engagements/${encodeURIComponent(engagementId)}/elements/` +
        `${encodeURIComponent(elementId)}/confirm`,
      { method: 'POST' },
    ),
  deactivateElement: (
    engagementId: string,
    elementId: string,
    body: ElementDeactivate = { acknowledge_active_children: false },
  ) =>
    request<EngagementElement>(
      `/engagements/${encodeURIComponent(engagementId)}/elements/` +
        `${encodeURIComponent(elementId)}/deactivate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  reactivateElement: (engagementId: string, elementId: string) =>
    request<EngagementElement>(
      `/engagements/${encodeURIComponent(engagementId)}/elements/` +
        `${encodeURIComponent(elementId)}/reactivate`,
      { method: 'POST' },
    ),

  /*
   * The owner picker's candidate list. There is no create beside it: an
   * account is minted on the Users screen, never from an ownership dialog.
   */
  getAssignableUsers: () => request<AssignableUserList>('/assignable-users'),

  /*
   * `node_type` and `node_id` are SELECTORS, not filters: the server refuses
   * an unqualified list, because the ownership graph is what phase 9 turns
   * into data scope.
   */
  getOwners: (nodeType: string, nodeId: string, includeEnded = false) =>
    request<DeliveryRoleAssignmentList>(
      `/delivery-role-assignments${queryString({
        node_type: nodeType,
        node_id: nodeId,
        include_ended: includeEnded ? 'true' : null,
      })}`,
    ),
  assignOwner: (assignment: DeliveryRoleAssignmentCreate) =>
    request<DeliveryRoleAssignment>('/delivery-role-assignments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(assignment),
    }),
  endAssignment: (assignmentId: string) =>
    request<DeliveryRoleAssignment>(
      `/delivery-role-assignments/${encodeURIComponent(assignmentId)}/end`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      },
    ),

  reactivateBusinessUnitGroup: (groupId: string) =>
    request<BusinessUnitGroup>(
      `/business-unit-groups/${encodeURIComponent(groupId)}/reactivate`,
      { method: 'POST' },
    ),

  /* Ingestion and background jobs (f8.1).

     `uploadBatch` sends multipart and deliberately sets NO Content-Type: the
     browser must add its own, including the boundary it generated, and a
     hand-written header overwrites that and produces a body the server cannot
     parse.

     There is no cancel, retry or delete method here, because the server
     exposes none -- a job row records what happened, and an operator
     re-uploads, which creates a new batch and leaves the failed one as
     evidence. */
  uploadBatch: (file: File, sourceSystem: string, datasetName?: string) => {
    const body = new FormData();
    body.append('file', file);
    body.append('source_system', sourceSystem);
    if (datasetName) body.append('dataset_name', datasetName);
    return request<BatchAccepted>('/ingestion/batches', { method: 'POST', body });
  },
  getIngestionBatch: (batchId: string) =>
    request<IngestionBatch>(`/ingestion/batches/${encodeURIComponent(batchId)}`),
  getJob: (jobId: string) => request<Job>(`/jobs/${encodeURIComponent(jobId)}`),
  listJobs: (query: JobQuery = {}) => request<JobList>(`/jobs${queryString(query)}`),

  /* The staged import plan (f8.2), and the decisions that release it (f8.3).

     Three reads, one abandonment, three decisions and one commit. There is
     still no delete and no retry: a staged row is evidence, and an operator
     who wants a different plan uploads again, which creates a new batch. */
  listIngestionBatches: (query: IngestionBatchQuery = {}) =>
    request<IngestionBatchList>(`/ingestion/batches${queryString(query)}`),
  getStagedPlan: (batchId: string) =>
    request<StagedPlan>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/staged-plan`,
    ),
  getRoleSlots: (batchId: string, states: readonly string[] = []) =>
    request<StagedRoleSlotList>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/role-slots` +
        queryString({ state: states }),
    ),
  discardBatch: (batchId: string) =>
    request<IngestionBatch>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/discard`,
      { method: 'POST' },
    ),

  /* The three decisions (f8.3).

     `normalized_key` travels in the BODY and never in the path: it contains
     spaces and non-ASCII letters, and it is a grouping key rather than an
     address. Every path segment below is a UUID. */
  resolveRoleHolder: (batchId: string, resolution: RoleHolderResolution) =>
    request<RoleHolderResolved>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/role-holder-resolutions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resolution),
      },
    ),
  chooseContestedHolder: (batchId: string, chosenStagedRoleId: string) =>
    request<ContestedSlotResolved>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/contested-slot-resolutions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chosen_staged_role_id: chosenStagedRoleId }),
      },
    ),
  setStagedNodeCode: (batchId: string, stagedNodeId: string, code: string) =>
    request<StagedNodeCodeResolved>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/staged-nodes/` +
        encodeURIComponent(stagedNodeId),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      },
    ),

  /* The commit. Returns `202` with a job id and writes nothing itself: the
     `master.commit` worker does the writing, in one transaction, and progress
     is read through the jobs endpoint that already exists. */
  commitBatch: (batchId: string) =>
    request<CommitAccepted>(
      `/ingestion/batches/${encodeURIComponent(batchId)}/commit`,
      { method: 'POST' },
    ),

  /* f10.1: one cycle and its two state changes. Finance/Controller and Admin
     only, enforced by the server; a stale `expected_version` is a `409
     CYCLE_VERSION_CONFLICT` carrying the current state. */
  getCycle: (cycleId: string) => request<Cycle>(`/cycles/${encodeURIComponent(cycleId)}`),

  /* f10.2: any platform role may list; only Finance/Admin preview and create. */
  listCycles: (query: CycleListQuery = {}) => {
    const params = new URLSearchParams();
    if (query.state) params.set('state', query.state);
    if (query.year !== undefined) params.set('year', String(query.year));
    const suffix = params.toString();
    return request<CycleList>(`/cycles${suffix ? `?${suffix}` : ''}`);
  },
  previewCycle: (year: number, month: number) =>
    request<CyclePreview>(
      `/cycles/preview?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`,
    ),
  createCycle: (body: CreateCycle) =>
    request<Cycle>('/cycles', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  transitionCycle: (cycleId: string, body: CycleTransition) =>
    request<Cycle>(`/cycles/${encodeURIComponent(cycleId)}/transitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  /* f10.9: the only way Prepared becomes Open for input. Finance/Admin only. */
  getCycleReleaseReadiness: (cycleId: string) =>
    request<ReleaseReadiness>(`/cycles/${encodeURIComponent(cycleId)}/release-readiness`),
  releaseCycle: (cycleId: string, body: ReleaseCycle) =>
    request<ReleaseCycleResult>(`/cycles/${encodeURIComponent(cycleId)}/release`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  /* f12.3: the lock. Freeze and its preview are Finance only; the snapshot
     history is Finance and Admin. All enforced server-side. */
  getCycleFreezePreview: (cycleId: string) =>
    request<FreezePreview>(`/cycles/${encodeURIComponent(cycleId)}/freeze-preview`),
  freezeCycle: (cycleId: string, body: FreezeCycle) =>
    request<Cycle>(`/cycles/${encodeURIComponent(cycleId)}/freeze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  listCycleSnapshots: (cycleId: string) =>
    request<CycleSnapshotList>(`/cycles/${encodeURIComponent(cycleId)}/snapshots`),
  /* f14.2: the reconciliation queue. Finance/Controller and Platform Admin; a decision is final. */
  listReconciliationVariances: (cycleId: string, query: ReconciliationQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return request<ReconciliationVarianceList>(
      `/reconciliation/cycles/${encodeURIComponent(cycleId)}/variances${suffix}`,
    );
  },
  /** `batchId` (f14.3) narrows the counts to the variances one ingest raised. */
  getReconciliationSummary: (cycleId: string, batchId?: string) =>
    request<ReconciliationSummary>(
      `/reconciliation/cycles/${encodeURIComponent(cycleId)}/summary${
        batchId ? `?batch_id=${encodeURIComponent(batchId)}` : ''
      }`,
    ),

  /* f14.3: step 7. Upload links the batch to the cycle; the list carries the
     expected feeds. Review, rows, commit and discard are f14.1's own routes,
     where the cycle precondition runs server-side. */
  uploadActualsStepBatch: (cycleId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<ActualsUploadAccepted>(`${actualsStepBase(cycleId)}/batches`, { method: 'POST', body });
  },
  listActualsStepBatches: (cycleId: string) => request<ActualsStepBatches>(`${actualsStepBase(cycleId)}/batches`),
  getActualsStepJob: (cycleId: string, jobId: string) =>
    request<Job>(`${actualsStepBase(cycleId)}/jobs/${encodeURIComponent(jobId)}`),
  getActualsBatch: (batchId: string) => request<ActualsReview>(`/actuals/batches/${encodeURIComponent(batchId)}`),
  listActualsBatchRows: (batchId: string, matchStatus: string, page = 1, pageSize = 50) =>
    request<ActualsStagedRows>(
      `/actuals/batches/${encodeURIComponent(batchId)}/rows?match_status=${encodeURIComponent(matchStatus)}` +
        `&page=${page}&page_size=${pageSize}`,
    ),
  commitActualsBatch: (batchId: string) =>
    request<ActualsCommitAccepted>(`/actuals/batches/${encodeURIComponent(batchId)}/commit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),
  discardActualsBatch: (batchId: string) =>
    request<ActualsBatchListItem>(`/actuals/batches/${encodeURIComponent(batchId)}/discard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),
  decideReconciliationVariance: (varianceId: string, body: ReconciliationDecision) =>
    request<ReconciliationVariance>(`/reconciliation/variances/${encodeURIComponent(varianceId)}/decision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  /* f13.1: the cycle CSV export. Requested by any platform role; the status and
     the file are the requester's only (anyone else gets 404). */
  requestCycleExport: (cycleId: string) =>
    request<CycleExportAccepted>(`/cycles/${encodeURIComponent(cycleId)}/exports`, { method: 'POST' }),
  getCycleExport: (cycleId: string, exportId: string) =>
    request<CycleExportStatus>(
      `/cycles/${encodeURIComponent(cycleId)}/exports/${encodeURIComponent(exportId)}`,
    ),
  downloadCycleExport: (cycleId: string, exportId: string) =>
    requestBlob(
      `/cycles/${encodeURIComponent(cycleId)}/exports/${encodeURIComponent(exportId)}/file`,
      'text/csv',
    ),
  /* f13.2: submit a frozen cycle (Finance only); the record and the exact
     submitted bytes are Finance and Admin. All enforced server-side. */
  submitCycle: (cycleId: string, body: SubmitCycle) =>
    request<SubmitCycleResult>(`/cycles/${encodeURIComponent(cycleId)}/submission`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  getCycleSubmission: (cycleId: string) =>
    request<CycleSubmission>(`/cycles/${encodeURIComponent(cycleId)}/submission`),
  /* f15.1: artifacts. The format is sniffed from the bytes by the server;
     the browser never inspects the file's extension or type. There is no
     delete and no version edit to call -- the server exposes neither. */
  listArtifactsForOwner: (ownerType: ArtifactListOwnerType, ownerId: string) =>
    request<ArtifactList>(`${ARTIFACT_OWNER_BASES[ownerType]}/${encodeURIComponent(ownerId)}/artifacts`),
  getArtifact: (artifactId: string) =>
    request<ArtifactDetail>(`/artifacts/${encodeURIComponent(artifactId)}`),
  /* f15.2: every visible artifact across the four levels. No parameters --
     filtering and paging happen in the browser. */
  listArtifacts: () => request<ArtifactList>('/artifacts'),
  listArtifactOwnerOptions: (level: ArtifactOwnerType) =>
    request<ArtifactOwnerOptionList>(`/artifacts/owner-options?level=${encodeURIComponent(level)}`),
  createArtifact: (input: NewArtifact) => {
    const body = new FormData();
    body.append('owner_type', input.ownerType);
    body.append('owner_id', input.ownerId);
    body.append('title', input.title);
    if (input.description) body.append('description', input.description);
    if (input.releaseNote) body.append('release_note', input.releaseNote);
    body.append('file', input.file);
    return request<ArtifactDetail>('/artifacts', { method: 'POST', body });
  },
  updateArtifact: (artifactId: string, update: ArtifactMetadataUpdate) =>
    request<ArtifactDetail>(`/artifacts/${encodeURIComponent(artifactId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    }),
  /* f15.3: the pointer moves on upload only when `publish` is explicitly true. */
  uploadArtifactVersion: (artifactId: string, file: File, releaseNote?: string, publish = false) => {
    const body = new FormData();
    body.append('file', file);
    if (releaseNote) body.append('release_note', releaseNote);
    if (publish) body.append('publish', 'true');
    return request<ArtifactVersion>(`/artifacts/${encodeURIComponent(artifactId)}/versions`, {
      method: 'POST',
      body,
    });
  },
  /* f15.3: point the external link at ANY version (publish, roll forward, roll back). */
  publishArtifactVersion: (artifactId: string, versionId: string) =>
    request<ArtifactDetail>(`/artifacts/${encodeURIComponent(artifactId)}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version_id: versionId }),
    }),
  /* f15.3: make the artifact Private; nothing is deleted. */
  unpublishArtifact: (artifactId: string, reason?: string) =>
    request<ArtifactDetail>(`/artifacts/${encodeURIComponent(artifactId)}/unpublish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  /* f15.4: access codes. List carries labels and states only; issue returns the
     plaintext ONCE; revoke touches one code. No read-one, edit or delete exists. */
  listArtifactAccessPasswords: (artifactId: string) =>
    request<AccessPasswordList>(`/artifacts/${encodeURIComponent(artifactId)}/access-passwords`),
  issueArtifactAccessPassword: (artifactId: string, input: IssueAccessPasswordRequest) =>
    request<IssuedAccessPassword>(`/artifacts/${encodeURIComponent(artifactId)}/access-passwords`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  revokeArtifactAccessPassword: (artifactId: string, passwordId: string, reason: string) =>
    request<AccessPassword>(
      `/artifacts/${encodeURIComponent(artifactId)}/access-passwords/${encodeURIComponent(passwordId)}/revoke`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    ),
  /* f15.6: the artifact's access history, keyset-paged, newest first. */
  listArtifactAccessEvents: (artifactId: string, params: { limit?: number; cursor?: string }) => {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor) query.set('cursor', params.cursor);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<ArtifactAccessEventPage>(`/artifacts/${encodeURIComponent(artifactId)}/access-events${suffix}`);
  },
  downloadArtifactVersion: (artifactId: string, versionId: string) =>
    requestBlob(
      `/artifacts/${encodeURIComponent(artifactId)}/versions/${encodeURIComponent(versionId)}/content`,
      'application/pdf, text/html',
    ),
  downloadCycleSubmission: (cycleId: string) =>
    requestBlob(`/cycles/${encodeURIComponent(cycleId)}/submission/file`, 'text/csv'),
  /* f10.3: the step rail. Finance/Admin only, enforced by the server. Apply
     answers `200` with the workflow (acknowledged) or `202 {job_id, step}`
     when an update job was enqueued. */
  getCycleWorkflow: (cycleId: string) =>
    request<CycleWorkflow>(`/cycles/${encodeURIComponent(cycleId)}/workflow`),
  acceptCycleStep: (cycleId: string, stepCode: string, body: StepChange) =>
    request<CycleWorkflow>(
      `/cycles/${encodeURIComponent(cycleId)}/steps/${encodeURIComponent(stepCode)}/accept`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  applyCycleStepUpdate: (cycleId: string, stepCode: string, body: StepChange) =>
    request<CycleWorkflow | ApplyAccepted>(
      `/cycles/${encodeURIComponent(cycleId)}/steps/${encodeURIComponent(stepCode)}/apply`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  reopenCycle: (cycleId: string, body: CycleReopen) =>
    request<Cycle>(`/cycles/${encodeURIComponent(cycleId)}/reopen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /* f10.4: step 1. Upload answers `202` and parses nothing; progress of the
     parse and the commit is read through the step's own job route, which
     Finance may call (the global jobs list is Admin-only). */
  uploadUsEngagementsBatch: (cycleId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<UsEngagementsUploadAccepted>(`${usEngagementsBase(cycleId)}/batches`, {
      method: 'POST',
      body,
    });
  },
  listUsEngagementsBatches: (cycleId: string) =>
    request<UsEngagementsBatchList>(`${usEngagementsBase(cycleId)}/batches`),
  getUsEngagementsBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsReview>(
      `${usEngagementsBase(cycleId)}/batches/${encodeURIComponent(batchId)}`,
    ),
  getUsEngagementsJob: (cycleId: string, jobId: string) =>
    request<Job>(`${usEngagementsBase(cycleId)}/jobs/${encodeURIComponent(jobId)}`),
  decideUsEngagementsItem: (
    cycleId: string,
    batchId: string,
    kind: UsEngagementsItemKind,
    itemId: string,
    body: AccountDecisionBody | EngagementDecisionBody | ElementDecisionBody,
  ) =>
    request<UsEngagementsDecisionResult>(
      `${usEngagementsBase(cycleId)}/batches/${encodeURIComponent(batchId)}/${kind}/` +
        `${encodeURIComponent(itemId)}/decision`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  decideUsEngagementsBulk: (cycleId: string, batchId: string, body: BulkDecisionBody) =>
    request<UsEngagementsDecisionResult>(
      `${usEngagementsBase(cycleId)}/batches/${encodeURIComponent(batchId)}/decisions/bulk`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  commitUsEngagementsBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsCommitAccepted>(
      `${usEngagementsBase(cycleId)}/batches/${encodeURIComponent(batchId)}/commit`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  discardUsEngagementsBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsBatch>(
      `${usEngagementsBase(cycleId)}/batches/${encodeURIComponent(batchId)}/discard`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),

  /* f10.5: step 2. Same shape as step 1: upload answers `202` and parses
     nothing; parse and commit progress are read through the step's job route. */
  uploadBillableDaysBatch: (cycleId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<UsEngagementsUploadAccepted>(`${billableDaysBase(cycleId)}/batches`, {
      method: 'POST',
      body,
    });
  },
  listBillableDaysBatches: (cycleId: string) =>
    request<UsEngagementsBatchList>(`${billableDaysBase(cycleId)}/batches`),
  getBillableDaysBatch: (cycleId: string, batchId: string) =>
    request<BillableDaysReview>(`${billableDaysBase(cycleId)}/batches/${encodeURIComponent(batchId)}`),
  getBillableDaysJob: (cycleId: string, jobId: string) =>
    request<Job>(`${billableDaysBase(cycleId)}/jobs/${encodeURIComponent(jobId)}`),
  decideBillableDaysCountry: (
    cycleId: string,
    batchId: string,
    itemId: string,
    body: BillableDaysDecisionBody,
  ) =>
    request<BillableDaysDecisionResult>(
      `${billableDaysBase(cycleId)}/batches/${encodeURIComponent(batchId)}/countries/` +
        `${encodeURIComponent(itemId)}/decision`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  commitBillableDaysBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsCommitAccepted>(
      `${billableDaysBase(cycleId)}/batches/${encodeURIComponent(batchId)}/commit`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  discardBillableDaysBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsBatch>(
      `${billableDaysBase(cycleId)}/batches/${encodeURIComponent(batchId)}/discard`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),

  /* f10.10: budget reference data. Two tables edited in place (PUT on the
     natural key, with the version read), and a CSV import that answers `202`
     and parses nothing -- progress is read through the budget job route. */
  listClientAnnualBudgets: (fiscalYear: number) =>
    request<ClientAnnualBudgetList>(`/budgets/client-annual${queryString({ fiscal_year: fiscalYear })}`),
  saveClientAnnualBudget: (clientId: string, fiscalYear: number, body: ClientAnnualBudgetSave) =>
    request<ClientAnnualBudgetItem>(`/budgets/client-annual/${encodeURIComponent(clientId)}/${fiscalYear}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  listUnitMonthlyBudgets: (fiscalYear: number) =>
    request<UnitMonthlyBudgetList>(`/budgets/unit-monthly${queryString({ fiscal_year: fiscalYear })}`),
  saveUnitMonthlyBudget: (unitId: string, fiscalYear: number, body: UnitMonthlyBudgetSave) =>
    request<UnitMonthlyBudgetItem>(`/budgets/unit-monthly/${encodeURIComponent(unitId)}/${fiscalYear}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  uploadBudgetImport: (kind: BudgetImportKind, file: File) => {
    const body = new FormData();
    body.append('file', file);
    body.append('kind', kind);
    return request<BudgetImportAccepted>('/budgets/imports', { method: 'POST', body });
  },
  listBudgetImports: () => request<BudgetImportList>('/budgets/imports'),
  getBudgetImport: (batchId: string) =>
    request<BudgetImportReview>(`/budgets/imports/${encodeURIComponent(batchId)}`),
  getBudgetImportJob: (jobId: string) => request<Job>(`/budgets/imports/jobs/${encodeURIComponent(jobId)}`),
  commitBudgetImport: (batchId: string) =>
    request<BudgetImportCommitAccepted>(`/budgets/imports/${encodeURIComponent(batchId)}/commit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),
  discardBudgetImport: (batchId: string) =>
    request<BudgetImportBatch>(`/budgets/imports/${encodeURIComponent(batchId)}/discard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /* f10.8: step 5. One feed, one scope: upload answers `202` and parses
     nothing; parse and commit progress are read through the step's job route. */
  uploadExchangeRateBatch: (cycleId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<UsEngagementsUploadAccepted>(`${exchangeRatesBase(cycleId)}/batches`, {
      method: 'POST',
      body,
    });
  },
  listExchangeRateBatches: (cycleId: string) =>
    request<UsEngagementsBatchList>(`${exchangeRatesBase(cycleId)}/batches`),
  getExchangeRateBatch: (cycleId: string, batchId: string) =>
    request<ExchangeRatesReview>(`${exchangeRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}`),
  getExchangeRateJob: (cycleId: string, jobId: string) =>
    request<Job>(`${exchangeRatesBase(cycleId)}/jobs/${encodeURIComponent(jobId)}`),
  commitExchangeRateBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsCommitAccepted>(
      `${exchangeRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/commit`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  discardExchangeRateBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsBatch>(
      `${exchangeRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/discard`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  /** The cycle's current rates -- any assigned role; empty until step 5 commits. */
  listCycleExchangeRates: (cycleId: string) =>
    request<CycleExchangeRates>(`/cycles/${encodeURIComponent(cycleId)}/exchange-rates`),

  /* f10.6: step 3. One upload per scope; the fixed-unit scope also names its
     Business Unit. Parse and commit progress are read through the step's job route. */
  listKimbleDumpScopes: (cycleId: string) => request<KimbleScopes>(`${kimbleDumpBase(cycleId)}/scopes`),
  uploadKimbleDumpBatch: (cycleId: string, file: File, scope: string, businessUnitId?: string) => {
    const body = new FormData();
    body.append('file', file);
    body.append('scope', scope);
    if (businessUnitId) body.append('business_unit_id', businessUnitId);
    return request<UsEngagementsUploadAccepted>(`${kimbleDumpBase(cycleId)}/batches`, {
      method: 'POST',
      body,
    });
  },
  listKimbleDumpBatches: (cycleId: string) => request<KimbleBatchList>(`${kimbleDumpBase(cycleId)}/batches`),
  getKimbleDumpBatch: (cycleId: string, batchId: string) =>
    request<KimbleReview>(`${kimbleDumpBase(cycleId)}/batches/${encodeURIComponent(batchId)}`),
  getKimbleDumpJob: (cycleId: string, jobId: string) =>
    request<Job>(`${kimbleDumpBase(cycleId)}/jobs/${encodeURIComponent(jobId)}`),
  decideKimbleResourceBu: (cycleId: string, batchId: string, itemId: string, body: KimbleResourceBuDecisionBody) =>
    request<KimbleResourceBuDecisionResult>(
      `${kimbleDumpBase(cycleId)}/batches/${encodeURIComponent(batchId)}/resource-bus/` +
        `${encodeURIComponent(itemId)}/decision`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  decideKimbleUnit: (cycleId: string, batchId: string, itemId: string, body: KimbleUnitDecisionBody) =>
    request<KimbleUnitDecisionResult>(
      `${kimbleDumpBase(cycleId)}/batches/${encodeURIComponent(batchId)}/units/${encodeURIComponent(itemId)}/decision`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  commitKimbleDumpBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsCommitAccepted>(
      `${kimbleDumpBase(cycleId)}/batches/${encodeURIComponent(batchId)}/commit`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  discardKimbleDumpBatch: (cycleId: string, batchId: string) =>
    request<KimbleBatch>(
      `${kimbleDumpBase(cycleId)}/batches/${encodeURIComponent(batchId)}/discard`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  /* f10.7: step 4. One upload per scope; parse and commit progress are read
     through the step's job route. */
  listSalesRateScopes: (cycleId: string) => request<SalesRateScopes>(`${salesRatesBase(cycleId)}/scopes`),
  uploadSalesRateBatch: (cycleId: string, file: File, scope: string) => {
    const body = new FormData();
    body.append('file', file);
    body.append('scope', scope);
    return request<UsEngagementsUploadAccepted>(`${salesRatesBase(cycleId)}/batches`, { method: 'POST', body });
  },
  listSalesRateBatches: (cycleId: string) => request<SalesRateBatchList>(`${salesRatesBase(cycleId)}/batches`),
  getSalesRateBatch: (cycleId: string, batchId: string) =>
    request<SalesRateReview>(`${salesRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}`),
  getSalesRateJob: (cycleId: string, jobId: string) =>
    request<Job>(`${salesRatesBase(cycleId)}/jobs/${encodeURIComponent(jobId)}`),
  decideSalesRateResourceBu: (cycleId: string, batchId: string, itemId: string, body: SalesRateBuDecisionBody) =>
    request<SalesRateBuDecisionResult>(
      `${salesRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/resource-bus/` +
        `${encodeURIComponent(itemId)}/decision`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  decideSalesRateElement: (cycleId: string, batchId: string, itemId: string, body: SalesRateElementDecisionBody) =>
    request<SalesRateElementDecisionResult>(
      `${salesRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/elements/` +
        `${encodeURIComponent(itemId)}/decision`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  decideAllSalesRateElements: (cycleId: string, batchId: string, body: SalesRateDecideAllBody) =>
    request<SalesRateDecideAllResult>(
      `${salesRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/elements/decide-all`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  commitSalesRateBatch: (cycleId: string, batchId: string) =>
    request<UsEngagementsCommitAccepted>(
      `${salesRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/commit`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  discardSalesRateBatch: (cycleId: string, batchId: string) =>
    request<SalesRateBatch>(
      `${salesRatesBase(cycleId)}/batches/${encodeURIComponent(batchId)}/discard`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
  /* f11.1: the forecast grid. Reads are scope-filtered by the server; a
     write carries the line's version and answers 409 with `current` when stale. */
  /* f11.3: the rail entry -- any platform role; counts are scoped by the server. */
  getCycleInputEntry: (cycleId: string) =>
    request<CycleInputEntry>(`/cycles/${encodeURIComponent(cycleId)}/input-entry`),
  listForecastGridUnits: (cycleId: string) =>
    request<ForecastGridUnits>(`/cycles/${encodeURIComponent(cycleId)}/forecast-grid/business-units`),
  getForecastGrid: (cycleId: string, businessUnitId: string) =>
    request<ForecastGrid>(
      `/cycles/${encodeURIComponent(cycleId)}/forecast-grid${queryString({ business_unit_id: businessUnitId })}`,
    ),
  /** f11.6: the visible lines of the cycle that carry validation warnings (the review screen). */
  listCycleValidationWarnings: (cycleId: string, filter: { businessUnitId?: string; clientId?: string } = {}) =>
    request<CycleValidationWarnings>(
      `/cycles/${encodeURIComponent(cycleId)}/validation-warnings${queryString({
        business_unit_id: filter.businessUnitId,
        client_id: filter.clientId,
      })}`,
    ),
  updateForecastLine: (cycleId: string, lineId: string, body: ForecastLineChange) =>
    request<ForecastLineUpdated>(
      `/cycles/${encodeURIComponent(cycleId)}/forecast-lines/${encodeURIComponent(lineId)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  /* f12.1: the review screen. Scope-filtered by the server; budget is Finance/Admin only. */
  /** f16.1: the scoped landing overview. */
  getDashboard: () => request<Dashboard>('/dashboard'),
  listReviewUnits: (cycleId: string) =>
    request<ReviewUnits>(`/cycles/${encodeURIComponent(cycleId)}/review/units`),
  getReview: (cycleId: string, unitIds: readonly string[]) => {
    const query = new URLSearchParams();
    unitIds.forEach((id) => query.append('unit', id));
    return request<Review>(`/cycles/${encodeURIComponent(cycleId)}/review?${query.toString()}`);
  },
  updateReviewRow: (cycleId: string, businessUnitId: string, clientId: string, body: ReviewRowChange) =>
    request<ReviewRowSaved>(reviewRowPath(cycleId, businessUnitId, clientId), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  listReviewComments: (cycleId: string, businessUnitId: string, clientId: string) =>
    request<ReviewCommentThread>(`${reviewRowPath(cycleId, businessUnitId, clientId)}/comments`),
  /* f11.2: the master elements the caller may add, and the add itself (id only, never text). */
  listForecastElementCandidates: (cycleId: string, businessUnitId: string, q?: string) =>
    request<ElementCandidates>(
      `/cycles/${encodeURIComponent(cycleId)}/forecast-elements/candidates${queryString({
        business_unit_id: businessUnitId,
        q: q || undefined,
      })}`,
    ),
  addForecastElement: (cycleId: string, body: AddForecastElementBody) =>
    request<ForecastElementAdded>(`/cycles/${encodeURIComponent(cycleId)}/forecast-lines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
};

function reviewRowPath(cycleId: string, businessUnitId: string, clientId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/review/rows/${encodeURIComponent(businessUnitId)}/${encodeURIComponent(clientId)}`;
}

function actualsStepBase(cycleId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/steps/ACTUALS`;
}

function exchangeRatesBase(cycleId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/steps/EXCHANGE_RATES`;
}

function salesRatesBase(cycleId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/steps/COSMA_SALES_RATES`;
}

function kimbleDumpBase(cycleId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/steps/KIMBLE_DUMP`;
}

function billableDaysBase(cycleId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/steps/BILLABLE_DAYS`;
}

function usEngagementsBase(cycleId: string): string {
  return `/cycles/${encodeURIComponent(cycleId)}/steps/US_ENGAGEMENTS`;
}
